import React, { useState, useEffect } from 'react';
import { Typography, Grid, Box, Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
    ProjectsContainer,
    ProjectCard,
    ProjectImage,
    ProjectOverlay,
    ModalContent,
    ModalImage,
    ModalNavigation,
} from './Projects.styles';
import axios from 'axios';

const backendUrl =
    import.meta.env.MODE === 'development'
        ? `http://${import.meta.env.VITE_BACKEND_URL}`
        : `https://${import.meta.env.VITE_BACKEND_URL_PROD}`;

const Projects = () => {
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await axios.get(`${backendUrl}/projects`);
                setProjects(response.data);
            } catch (error) {
                console.error('Error fetching projects:', error);
            }
        };

        fetchProjects();
    }, []);

    const handleProjectClick = (project) => {
        setSelectedProject(project);
        setCurrentImageIndex(0);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handlePrevImage = (e) => {
        e.stopPropagation();
        if (currentImageIndex > 0) {
            setCurrentImageIndex(currentImageIndex - 1);
        } else {
            setCurrentImageIndex(selectedProject.images.length - 1);
        }
    };

    const handleNextImage = (e) => {
        e.stopPropagation();
        if (currentImageIndex < selectedProject?.images.length - 1) {
            setCurrentImageIndex(currentImageIndex + 1);
        } else {
            setCurrentImageIndex(0);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                height: 'auto',
                py: { xs: 8, md: 12 }, // Add padding top/bottom (smaller on mobile, larger on desktop)
            }}
        >
            <ProjectsContainer id="projects">
                <Typography
                    variant="h2"
                    component="h2"
                    align="center"
                    color="text.primary"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 6 }}
                >
                    Our Projects
                </Typography>

                <Typography
                    variant="h5"
                    align="center"
                    color="text.secondary"
                    sx={{ mb: 8, maxWidth: '800px', mx: 'auto' }}
                >
                    Take a look at some of our recent projects. Quality
                    workmanship and attention to detail are our top priorities.
                </Typography>

                <Grid container spacing={3}>
                    {projects.map((project) => (
                        <Grid item xs={12} sm={6} md={4} key={project.id}>
                            <ProjectCard
                                onClick={() => handleProjectClick(project)}
                            >
                                <ProjectImage
                                    src={
                                        import.meta.env.MODE === 'development'
                                            ? project.images[0]
                                            : `${backendUrl}/media/projects/${project.images[0]
                                                  .split('/')
                                                  .pop()}`
                                    }
                                    alt={project.title}
                                />
                                <ProjectOverlay>
                                    <Typography
                                        variant="h6"
                                        component="h3"
                                        color="white"
                                    >
                                        {project.title}
                                    </Typography>
                                    <Typography variant="body2" color="white">
                                        Click to view
                                    </Typography>
                                </ProjectOverlay>
                            </ProjectCard>
                        </Grid>
                    ))}
                </Grid>

                {/* Project Modal */}
                <Modal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    aria-labelledby="project-modal-title"
                    aria-describedby="project-modal-description"
                >
                    <ModalContent>
                        <IconButton
                            onClick={handleCloseModal}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: 'white',
                                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        {selectedProject && (
                            <>
                                <ModalImage
                                    src={
                                        import.meta.env.MODE === 'development'
                                            ? selectedProject.images[
                                                  currentImageIndex
                                              ]
                                            : `${backendUrl}/media/projects/${selectedProject.images[
                                                  currentImageIndex
                                              ]
                                                  .split('/')
                                                  .pop()}`
                                    }
                                />

                                <ModalNavigation>
                                    <IconButton
                                        onClick={handlePrevImage}
                                        color="primary"
                                    >
                                        <ArrowBackIosNewIcon />
                                    </IconButton>

                                    <Box sx={{ px: 2 }}>
                                        <Typography
                                            variant="h5"
                                            id="project-modal-title"
                                            gutterBottom
                                        >
                                            {selectedProject.title}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            id="project-modal-description"
                                        >
                                            {selectedProject.description}
                                        </Typography>
                                    </Box>

                                    <IconButton
                                        onClick={handleNextImage}
                                        color="primary"
                                    >
                                        <ArrowForwardIosIcon />
                                    </IconButton>
                                </ModalNavigation>
                            </>
                        )}
                    </ModalContent>
                </Modal>
            </ProjectsContainer>
        </Box>
    );
};

export default Projects;
